import { useField } from "formik";
import { useCallback, useEffect } from "react";

import type { ColorPickerInputProps } from "../../color";
import { GradientPickerInput } from "../../color/GradientPickerInput";
import { useFieldContext } from "../FormFieldContext";
import { useCommitChange, useFieldLabelAndDescription } from "./helpers";

type FieldGradientPickerProps = Omit<ColorPickerInputProps, "value" | "onChange"> & {
  onChangeCommitted?: (value?: string, e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  hideLabels?: boolean;
};

export const FieldGradientPicker = ({ onChangeCommitted, hideLabels, ...props }: FieldGradientPickerProps) => {
  const { controlId, name } = useFieldContext();
  const [label, helperText] = useFieldLabelAndDescription(name);
  const [{ value }, _, { setValue, setError }] = useField<string>(name);
  const commitChange = useCommitChange();

  const handleChange = useCallback(
    (color: string) => {
      setValue(color).catch((error) => {
        setError(error);
        console.warn(error);
      });
    },
    [setError, setValue],
  );

  useEffect(() => {
    if (!value) return;

    commitChange();
    onChangeCommitted?.(value);
  }, [commitChange, onChangeCommitted, value]);

  return (
    <GradientPickerInput
      id={controlId}
      name={name}
      value={value}
      label={!hideLabels ? label : undefined}
      helperText={!hideLabels ? helperText : undefined}
      {...props}
      onChange={handleChange}
      setError={setError}
    />
  );
};
